import type { CSSProperties, ReactNode } from "react";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";

// antd Row/Col/Space -> MUI Grid/Stack. antd uses a 24-column grid, so the MUI
// Grid container is set to columns={24} and antd span/xs/sm/... transfer 1:1.
// gutter/size (px or token) -> MUI spacing (theme units = px/8).

const PX = 8;
const toSpacing = (g?: number | [number, number] | string): number | undefined => {
  if (g === undefined) return undefined;
  if (typeof g === "string") return { small: 1, middle: 2, large: 3 }[g] ?? 1;
  if (Array.isArray(g)) return g[0] / PX;
  return g / PX;
};

type Justify = "start" | "end" | "center" | "space-around" | "space-between";
type Align = "top" | "middle" | "bottom";
const JUSTIFY: Record<Justify, string> = {
  start: "flex-start", end: "flex-end", center: "center",
  "space-around": "space-around", "space-between": "space-between",
};
const ALIGN: Record<Align, string> = { top: "flex-start", middle: "center", bottom: "flex-end" };

export interface RowProps {
  children?: ReactNode;
  gutter?: number | [number, number];
  justify?: Justify;
  align?: Align;
  wrap?: boolean;
  style?: CSSProperties;
  className?: string;
  onClick?: () => void;
}

export const Row = ({ children, gutter, justify, align, wrap, style, className, onClick }: RowProps) => {
  const spacing = toSpacing(gutter);
  const rowSpacing = Array.isArray(gutter) ? gutter[1] / PX : undefined;
  return (
    <Grid
      container
      columns={24}
      spacing={rowSpacing === undefined ? spacing : undefined}
      columnSpacing={rowSpacing !== undefined ? spacing : undefined}
      rowSpacing={rowSpacing}
      justifyContent={justify ? JUSTIFY[justify] : undefined}
      alignItems={align ? ALIGN[align] : undefined}
      wrap={wrap === false ? "nowrap" : "wrap"}
      style={style}
      className={className}
      onClick={onClick}
    >
      {children}
    </Grid>
  );
};

export interface ColProps {
  children?: ReactNode;
  span?: number;
  offset?: number;
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  flex?: string | number;
  style?: CSSProperties;
  className?: string;
  onClick?: () => void;
}

export const Col = ({ children, span, offset, xs, sm, md, lg, xl, flex, style, className, onClick }: ColProps) => (
  <Grid
    item
    xs={xs ?? span ?? (flex ? undefined : 24)}
    sm={sm}
    md={md}
    lg={lg}
    xl={xl}
    style={{ ...(offset ? { marginLeft: `${(offset / 24) * 100}%` } : {}), ...(flex ? { flex } : {}), ...style }}
    className={className}
    onClick={onClick}
  >
    {children}
  </Grid>
);

export interface SpaceProps {
  children?: ReactNode;
  size?: number | "small" | "middle" | "large";
  direction?: "horizontal" | "vertical";
  align?: "start" | "end" | "center" | "baseline";
  wrap?: boolean;
  style?: CSSProperties;
  className?: string;
  "data-cy"?: string;
  "data-testid"?: string;
}

const SPACE_ALIGN: Record<string, string> = {
  start: "flex-start", end: "flex-end", center: "center", baseline: "baseline",
};

export const Space = ({ children, size = "small", direction = "horizontal", align, wrap, style, className, "data-cy": dataCy, "data-testid": dataTestid }: SpaceProps) => (
  <Stack
    direction={direction === "vertical" ? "column" : "row"}
    spacing={toSpacing(size) ?? 1}
    alignItems={align ? SPACE_ALIGN[align] : direction === "horizontal" ? "center" : undefined}
    flexWrap={wrap ? "wrap" : undefined}
    useFlexGap={wrap}
    style={style}
    className={className}
    data-cy={dataCy}
    data-testid={dataTestid}
  >
    {children}
  </Stack>
);
